import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DocumentFileIcon } from '../../../assets/icons/document-file.svg';
import { LedgerEntry } from '../../../interface/rfid-management.interface';
import MyButton from '../../../shared-components/button/Button';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import rfidApi from '../../../utils/api/rfid';

interface LedgerDetailsModalProperties {
  entry: LedgerEntry;
  ledgerId?: string;
  onClose: () => void;
}

/**
 * Safely extracts numeric balance or energy amount in kWh from decimal object, number, or string
 * @param {any} amountKwh - Raw energy amount value
 * @returns {number} Formatted numeric amount in kWh
 */
const getNumericAmount = (amountKwh: any): number => {
  if (typeof amountKwh === 'object' && amountKwh !== null && '$numberDecimal' in amountKwh) {
    return Number.parseFloat(amountKwh.$numberDecimal) || 0;
  }
  if (typeof amountKwh === 'number') {
    return amountKwh;
  }
  if (typeof amountKwh === 'string') {
    return Number.parseFloat(amountKwh) || 0;
  }
  return 0;
};

/**
 * Details view modal for a Business Tap Ledger entry
 * @param {LedgerDetailsModalProperties} properties - Component properties
 * @returns {React.JSX.Element} Ledger details modal content
 */
function LedgerDetailsModal(properties: LedgerDetailsModalProperties): React.JSX.Element {
  const { entry, ledgerId: ledgerIdProperty, onClose } = properties;
  const { t, i18n } = useTranslation(['devices', 'sharedComponent']);

  const [currentEntry, setCurrentEntry] = useState<LedgerEntry>(entry);
  const [ledgerLoading, setLedgerLoading] = useState<boolean>(false);

  useEffect(() => {
    setCurrentEntry(entry);
  }, [entry]);

  useEffect(() => {
    let isMounted = true;
    const targetLedgerId = ledgerIdProperty || entry._id || entry.id;

    if (targetLedgerId) {
      setLedgerLoading(true);
      rfidApi
        .fetchLedgerById(targetLedgerId)
        .then((response: any) => {
          if (!isMounted) return;
          const data =
            response?.data?.results || response?.data?.data || response?.data || response;
          if (data && typeof data === 'object') {
            const rawTimestamp = data?.timestamp || data?.createdAt;
            const formattedTimestamp = rawTimestamp ? ConvertUTCtoDate(rawTimestamp) : '';

            const formattedEntry: LedgerEntry = {
              ...data,
              id: data.id || data._id || targetLedgerId,
              _id: data._id || data.id || targetLedgerId,
              amountKwh: getNumericAmount(data.amountKwh),
              timestamp: formattedTimestamp,
              createdAt: data?.createdAt ? ConvertUTCtoDate(data.createdAt) : formattedTimestamp,
              updatedAt: data?.updatedAt ? ConvertUTCtoDate(data.updatedAt) : '',
            };
            setCurrentEntry(formattedEntry);
          }
        })
        .catch(() => {
          // Keep current entry on failure
        })
        .finally(() => {
          if (isMounted) {
            setLedgerLoading(false);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [entry._id, entry.id, ledgerIdProperty]);

  const isCredit = currentEntry.direction === 'CREDIT';
  const numericAmount = getNumericAmount(currentEntry.amountKwh);
  const rawTimestamp = currentEntry.timestamp || currentEntry.createdAt || '';
  const displayDateTime = rawTimestamp ? ConvertUTCtoDateTime(rawTimestamp) || rawTimestamp : '—';

  if (ledgerLoading && !currentEntry.id && !currentEntry._id) {
    return (
      <div className="rfid-entity-details" dir={i18n.dir()}>
        <div className="rfid-entity-details__body">
          <div className="rfid-stations-loading">
            <span className="rfid-stations-loading__spinner" />
            <span>{t('loadingLedgerDetails', { defaultValue: 'Loading ledger entry details...' })}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rfid-entity-details" dir={i18n.dir()}>
      <div className="rfid-entity-details__body">
        <div className="rfid-entity-details__header">
          <div className="rfid-entity-details__avatar rfid-entity-details__avatar--blue">
            <DocumentFileIcon />
          </div>
          <div className="rfid-entity-details__header-text">
            <h3>{currentEntry.id || currentEntry._id}</h3>
            <div className="rfid-entity-details__badges">
              <span
                className={`rfid-direction-badge ${
                  isCredit ? 'rfid-direction-badge--credit' : 'rfid-direction-badge--debit'
                }`}>
                <span className="numeric-unit" dir="ltr">
                  {isCredit ? '+' : '-'}
                </span>{' '}
                <span>
                  {isCredit
                    ? t('creditValue', { defaultValue: 'CREDIT' })
                    : t('debitValue', { defaultValue: 'DEBIT' })}
                </span>
              </span>
              <span
                className={`rfid-table-energy-amount ${
                  isCredit ? 'rfid-table-energy-amount--credit' : 'rfid-table-energy-amount--debit'
                }`}>
                <span className="numeric-unit" dir="ltr">
                  {isCredit ? '+' : '-'}
                  {Math.abs(numericAmount).toFixed(2)} kWh
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="rfid-entity-details__grid">
          <div className="rfid-entity-details__item">
            <span className="rfid-entity-details__label">
              {t('transactionId', { defaultValue: 'Transaction ID' })}
            </span>
            <span className="rfid-entity-details__value rfid-entity-details__mono">
              {currentEntry.id || currentEntry._id}
            </span>
          </div>

          <div className="rfid-entity-details__item">
            <span className="rfid-entity-details__label">
              {t('timestamp', { defaultValue: 'Timestamp (UTC)' })}
            </span>
            <span className="rfid-entity-details__value">{displayDateTime}</span>
          </div>

          <div className="rfid-entity-details__item">
            <span className="rfid-entity-details__label">
              {t('direction', { defaultValue: 'Transaction Direction' })}
            </span>
            <span className="rfid-entity-details__value">{currentEntry.direction}</span>
          </div>

          <div className="rfid-entity-details__item">
            <span className="rfid-entity-details__label">
              {t('energyKwh', { defaultValue: 'Transacted Energy' })}
            </span>
            <span className="rfid-entity-details__value">
              {numericAmount.toFixed(2)} kWh
            </span>
          </div>

          {currentEntry.cardPoolName && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('cardGroupName', { defaultValue: 'Card Group Name' })}
              </span>
              <span className="rfid-entity-details__value">{currentEntry.cardPoolName}</span>
            </div>
          )}

          {currentEntry.cardPoolId && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('cardGroupId', { defaultValue: 'Card Group ID' })}
              </span>
              <span className="rfid-entity-details__value rfid-entity-details__mono">
                {currentEntry.cardPoolId}
              </span>
            </div>
          )}

          {(currentEntry.tokenUid || currentEntry.tokenId) && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('tokenUid', { defaultValue: 'Card UID (Physical Token)' })}
              </span>
              <span className="rfid-entity-details__value rfid-entity-details__mono">
                {currentEntry.tokenUid || currentEntry.tokenId}
              </span>
            </div>
          )}

          {currentEntry.amountCurrency !== null && currentEntry.amountCurrency !== undefined && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('costInformational', { defaultValue: 'CPO CDR Cost (Informational)' })}
              </span>
              <span className="rfid-entity-details__value">
                ${Number(currentEntry.amountCurrency).toFixed(2)}
              </span>
            </div>
          )}

          {(currentEntry.stationName || currentEntry.stationId) && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('station', { defaultValue: 'Charging Station' })}
              </span>
              <span className="rfid-entity-details__value">
                {currentEntry.stationName || currentEntry.stationId}
              </span>
            </div>
          )}

          {currentEntry.sessionId && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('sessionId', { defaultValue: 'OCPI Session ID' })}
              </span>
              <span className="rfid-entity-details__value rfid-entity-details__mono">
                {currentEntry.sessionId}
              </span>
            </div>
          )}

          {currentEntry.cdrReference && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('cdrReference', { defaultValue: 'CDR Reference' })}
              </span>
              <span className="rfid-entity-details__value rfid-entity-details__mono">
                {currentEntry.cdrReference}
              </span>
            </div>
          )}

          {(currentEntry as any).tenantId && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('owningTenantId', { defaultValue: 'Owning Tenant ID' })}
              </span>
              <span className="rfid-entity-details__value rfid-entity-details__mono">
                {(currentEntry as any).tenantId}
              </span>
            </div>
          )}

          {currentEntry.createdAt && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('createdDate', { defaultValue: 'Created Date' })}
              </span>
              <span className="rfid-entity-details__value">{currentEntry.createdAt}</span>
            </div>
          )}

          {currentEntry.updatedAt && (
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('lastUpdated', { defaultValue: 'Last Updated' })}
              </span>
              <span className="rfid-entity-details__value">{currentEntry.updatedAt}</span>
            </div>
          )}
        </div>
      </div>

      <div className="rfid-entity-details__actions">
        <MyButton
          label={t('close', { defaultValue: 'Close' })}
          buttonType="outline"
          onClickFunc={onClose}
        />
      </div>
    </div>
  );
}

export default LedgerDetailsModal;
